import React from 'react';
import { Alert, Checkbox, Form, Select, Upload } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import {
  DELIVERY_TERM_OPTIONS,
  MassiveScope,
  MAX_MASSIVE_ROWS,
  needsMarketplace,
  needsStore,
  SHIPMENT_TYPE_OPTIONS,
  storeIsRequired,
} from './options';

export interface Option {
  value: number;
  label: string;
}

export interface UploadFormValues {
  marketplaceId?: number;
  storeId?: number;
  defaultShipmentType?: string;
  defaultDeliveryTerm?: string;
  allowPartial: boolean;
}

interface Props {
  scope: MassiveScope;
  values: UploadFormValues;
  onChange: (values: UploadFormValues) => void;
  marketplaces: Option[];
  stores: Option[];
  storesLoading?: boolean;
  file?: UploadFile;
  onFileChange: (file?: UploadFile) => void;
}

const MassiveUploadForm = ({
  scope,
  values,
  onChange,
  marketplaces,
  stores,
  storesLoading,
  file,
  onFileChange,
}: Props) => {
  const set = (patch: Partial<UploadFormValues>) =>
    onChange({ ...values, ...patch });

  const showMarketplace = needsMarketplace(scope);
  const showStore = needsStore(scope);

  return (
    <Form layout="vertical">
      {showMarketplace ? (
        <Form.Item label="Marketplace" required>
          <Select
            placeholder="Seleccioná un marketplace"
            options={marketplaces}
            value={values.marketplaceId}
            onChange={(marketplaceId) =>
              set({ marketplaceId, storeId: undefined })
            }
            showSearch
            optionFilterProp="label"
          />
        </Form.Item>
      ) : null}

      {showStore ? (
        <Form.Item
          label="Tienda"
          required={storeIsRequired(scope)}
          extra="Todos los paquetes del archivo se crean en esta tienda."
        >
          <Select
            placeholder="Seleccioná una tienda"
            options={stores}
            value={values.storeId}
            onChange={(storeId) => set({ storeId })}
            loading={storesLoading}
            disabled={needsMarketplace(scope) && !values.marketplaceId}
            showSearch
            optionFilterProp="label"
          />
        </Form.Item>
      ) : null}

      <Form.Item
        label="Tipo de envío por defecto"
        extra="Se aplica sólo a las filas que tengan la columna vacía."
      >
        <Select
          allowClear
          placeholder="— sin valor por defecto —"
          options={SHIPMENT_TYPE_OPTIONS}
          value={values.defaultShipmentType}
          onChange={(defaultShipmentType) => set({ defaultShipmentType })}
        />
      </Form.Item>

      {values.defaultShipmentType === 'F' ? (
        <Alert
          type="warning"
          showIcon
          message="Con Flex por defecto, las filas sin Codigo_Adicional se rechazan."
          style={{ marginBottom: 16 }}
        />
      ) : null}

      <Form.Item
        label="Plazo de entrega por defecto"
        extra="Se aplica sólo a las filas que tengan la columna vacía."
      >
        <Select
          allowClear
          placeholder="— sin valor por defecto —"
          options={DELIVERY_TERM_OPTIONS}
          value={values.defaultDeliveryTerm}
          onChange={(defaultDeliveryTerm) => set({ defaultDeliveryTerm })}
        />
      </Form.Item>

      <Form.Item label="Archivo" required>
        <Upload.Dragger
          accept=".xlsx"
          maxCount={1}
          beforeUpload={(selected) => {
            onFileChange(selected as unknown as UploadFile);
            return false;
          }}
          onRemove={() => onFileChange(undefined)}
          fileList={file ? [file] : []}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Arrastrá el archivo acá o buscalo</p>
          <p className="ant-upload-hint">
            .xlsx · hasta {MAX_MASSIVE_ROWS} filas por masiva
          </p>
        </Upload.Dragger>
      </Form.Item>

      <Form.Item
        extra={
          'Si está desmarcado y alguna fila no pasa la validación, no se crea ningún paquete. ' +
          'Marcado, se crean las filas válidas y te devolvemos las rechazadas en un Excel.'
        }
      >
        <Checkbox
          checked={values.allowPartial}
          onChange={(e) => set({ allowPartial: e.target.checked })}
        >
          Crear con fallas
        </Checkbox>
      </Form.Item>
    </Form>
  );
};

export default MassiveUploadForm;
